import { ClassificationGroup } from '@core/generated/gql-types';

export const formatCurrency = (cents: number | null | undefined): string => {
  if (!cents) return '-';
  return `$${(cents / 100).toFixed(2)}`;
};

/**
 * Both `marginOverridePercentage` and `tenantMarginPercentage` are decimals
 * in the 0-1 range (e.g. 0.06 for 6%), matching what the API stores.
 */
export const calculateMarginChargeRate = (
  minimum: number | null | undefined,
  marginOverridePercentage: number | null | undefined,
  tenantMarginPercentage: number | null | undefined,
): number | null => {
  if (!minimum) return null;
  const margin = marginOverridePercentage ?? tenantMarginPercentage ?? 0;
  return minimum + Math.round(minimum * margin);
};

const LEVEL_NUMBER = /\blevel\s*(\d+)\b/i;
const LEVEL_NUMBER_STEP = /\blevel\s*\d+\s*\(([a-d])\)/i;
const HOSPITALITY_STEP = /^(barstaff|wait staff|kitchen hand)\s+(\d+)\b/i;
const RANK_NUMBER = /^\s*rank\s*(\d+)\s*$/i;
const GRADE_NUMBER = /\bgrade\s*(\d+)\b/i;
const CW_NUMBER = /\bcw\s*(\d+)\b/i;
const CERT_ROMAN = /^\s*cert(?:ificate)?\s*(iii|ii|iv|i|v)\s*$/i;
const LABOURER_STEP = /\blvl\s*([a-d])\b/i;
const APPRENTICE_CODE = /\bl(\d+)y(\d+)\b/i;
const CLERICAL_LEVEL = /\bclerical\s*l(\d+)\b/i;
const JUNIOR_AGE = /^junior\s+(\d+)$/i;

const ROMAN_STEPS: Record<string, number> = {
  i: 1,
  ii: 2,
  iii: 3,
  iv: 4,
  v: 5,
};

/**
 * Exact names only. A name that merely contains one of these words
 * (`Junior Crane Operator`) is a job title, not a step on this scale.
 */
const NAMED_BANDS = [
  'entry level',
  'junior',
  'mid',
  'mid-level',
  'intermediate',
  'senior',
  'expert',
];

/** `a` = 1 … `d` = 4. */
const letterStep = (letter: string): number =>
  letter.toLowerCase().charCodeAt(0) - 'a'.charCodeAt(0) + 1;

export const parseLevelNumber = (name?: string | null): number | null => {
  const match = name?.match(LEVEL_NUMBER);
  return match ? Number(match[1]) : null;
};

interface LevelRank {
  family: string;
  key: number;
  step: number;
}

/**
 * Classification levels have no `order` field, so display order is derived
 * from the name. Names only ever compare against others in the same family:
 * `Level 2` is not comparable to `Rank 2` or `Grade 2`, and names with no
 * recognisable ladder (job titles, shift names) are left where they are.
 */
const rankLevelName = (name?: string | null): LevelRank | null => {
  const value = name?.trim();
  if (!value) return null;

  const hospitality = value.match(HOSPITALITY_STEP);
  if (hospitality) {
    return {
      family: `hospitality:${hospitality[1]!.toLowerCase()}`,
      key: Number(hospitality[2]),
      step: 0,
    };
  }

  const rank = value.match(RANK_NUMBER);
  if (rank) return { family: 'rank', key: Number(rank[1]), step: 0 };

  const grade = value.match(GRADE_NUMBER);
  if (grade) return { family: 'grade', key: Number(grade[1]), step: 0 };

  const constructionWorker = value.match(CW_NUMBER);
  if (constructionWorker) {
    return { family: 'cw', key: Number(constructionWorker[1]), step: 0 };
  }

  const cert = value.match(CERT_ROMAN);
  if (cert) {
    return {
      family: 'cert',
      key: ROMAN_STEPS[cert[1]!.toLowerCase()]!,
      step: 0,
    };
  }

  const labourer = value.match(LABOURER_STEP);
  if (labourer) {
    return { family: 'labourer', key: letterStep(labourer[1]!), step: 0 };
  }

  const apprentice = value.match(APPRENTICE_CODE);
  if (apprentice) {
    return {
      family: 'apprentice',
      key: Number(apprentice[1]),
      step: Number(apprentice[2]),
    };
  }

  const clerical = value.match(CLERICAL_LEVEL);
  if (clerical) {
    return { family: 'clerical', key: Number(clerical[1]), step: 0 };
  }

  const levelNumber = parseLevelNumber(value);
  if (levelNumber !== null) {
    const step = value.match(LEVEL_NUMBER_STEP);
    return {
      family: 'level',
      key: levelNumber,
      step: step ? letterStep(step[1]!) : 0,
    };
  }

  const bandIndex = NAMED_BANDS.indexOf(value.toLowerCase());
  if (bandIndex !== -1) {
    return { family: 'named-band', key: bandIndex, step: 0 };
  }

  const juniorAge = value.match(JUNIOR_AGE);
  if (juniorAge)
    return { family: 'junior-age', key: Number(juniorAge[1]), step: 0 };

  return null;
};

export const sortLevelsForRates = <T extends { name?: string | null }>(
  levels: T[],
): T[] => {
  const families = new Map<
    string,
    Array<{ level: T; rank: LevelRank; index: number }>
  >();

  levels.forEach((level, index) => {
    const rank = rankLevelName(level.name);
    if (!rank) return;
    const members = families.get(rank.family) ?? [];
    members.push({ level, rank, index });
    families.set(rank.family, members);
  });

  const sortedLevels = [...levels];

  families.forEach((members) => {
    // Members are collected in ascending index order, so these are the slots
    // this family owns. Sorted members go back into them and nowhere else.
    const slots = members.map((member) => member.index);
    const ordered = [...members].sort(
      (first, second) =>
        first.rank.key - second.rank.key ||
        first.rank.step - second.rank.step ||
        first.index - second.index,
    );

    slots.forEach((slot, position) => {
      sortedLevels[slot] = ordered[position]!.level;
    });
  });

  return sortedLevels;
};

export const filterAndSortClassificationGroupsForClient = (
  groups: ClassificationGroup[],
  clientClassifications:
    Array<{ id?: string | null } | null> | null | undefined,
): ClassificationGroup[] => {
  const assignedIds = new Set(
    (clientClassifications ?? [])
      .map((classification) => classification?.id)
      .filter((id): id is string => Boolean(id)),
  );

  return groups
    .map((group) => {
      const classifications = (group.classifications ?? [])
        .filter(
          (classification) =>
            classification.id != null && assignedIds.has(classification.id),
        )
        .slice()
        .sort((first, second) =>
          (first.name ?? '').localeCompare(second.name ?? '', undefined, {
            sensitivity: 'base',
          }),
        )
        .map((classification) => ({
          ...classification,
          classificationLevels: sortLevelsForRates(
            classification.classificationLevels ?? [],
          ),
        }));

      return { ...group, classifications };
    })
    .filter((group) => (group.classifications?.length ?? 0) > 0);
};
